import { PORTAL_API_BASE_URL } from '../../../../constants';
import request from '../../../../util/APIUtils';

/**
 * Client for the portal LMS bulk-provisioning + job-monitoring APIs (CC-14598).
 * All endpoints are super_admin gated server-side; the UI mirrors that gating.
 * Response bodies are the raw portal DTOs (no envelope).
 */
const ADMIN = `${PORTAL_API_BASE_URL}/api/admin`;
const LMS = `${ADMIN}/lms`;

const req = (opts) => request({ ...opts, throw400Error: true });

const withPaging = (url, { limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
};

/** Portal validates facilityIds as numbers; grid/API values may arrive as strings. */
const normalizeFacilityIds = (facilityIds) => {
  if (!Array.isArray(facilityIds)) return [];
  return facilityIds
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
};

/**
 * POST /lms/locations/bulk-provision — provision facilities in bulk
 * (ALL or explicit list). Retained for future fleet-wide rollouts; the
 * single-facility "Provision Facility" button now uses the incremental
 * {@link provisionFacilityToCanvas} endpoint instead.
 */
export const bulkProvisionLocations = ({ scope = 'ALL', facilityIds } = {}) => {
  const body =
    scope === 'FACILITIES'
      ? { scope, facilityIds: normalizeFacilityIds(facilityIds) }
      : { scope };
  return req({ url: `${LMS}/locations/bulk-provision`, method: 'POST', body: JSON.stringify(body) });
};

/**
 * POST /facilities/{id}/provision-canvas — incremental, single-facility
 * Canvas provisioning. Walks the facility's ancestor chain and creates one
 * Canvas sub-account per unprovisioned link **synchronously, top-down**, so
 * each child nests under its real Canvas parent (no parallel-SQS race).
 * Returns a readiness snapshot (`chain[]`, `chainReady`) plus the side-effects
 * performed (`slasCreated`, `provisionedLocations`). Idempotent.
 */
export const provisionFacilityToCanvas = (facilityId) =>
  req({
    url: `${ADMIN}/facilities/${encodeURIComponent(facilityId)}/provision-canvas`,
    method: 'POST',
    body: JSON.stringify({}),
  });

/**
 * GET /facilities/{id}/provision-canvas — read-only readiness snapshot for
 * the facility *and* its ancestor chain. Drives the per-location indicators
 * in the provisioning panel and the Switch-to-Canvas gating without forcing
 * an action.
 */
export const fetchFacilityProvisionStatus = (facilityId) =>
  req({
    url: `${ADMIN}/facilities/${encodeURIComponent(facilityId)}/provision-canvas`,
    method: 'GET',
  });

/** POST /facilities/{id}/learners/bulk-provision — provision a facility's existing learners. */
export const bulkProvisionLearners = (facilityId) =>
  req({
    url: `${ADMIN}/facilities/${encodeURIComponent(facilityId)}/learners/bulk-provision`,
    method: 'POST',
    body: JSON.stringify({}),
  });

/** GET /lms/jobs — recent jobs across all facilities. */
export const fetchJobs = (paging) => req({ url: withPaging(`${LMS}/jobs`, paging), method: 'GET' });

/** GET /lms/jobs/{id} — a single job's status + counts. */
export const fetchJob = (jobId) => req({ url: `${LMS}/jobs/${encodeURIComponent(jobId)}`, method: 'GET' });

/** GET /facilities/{id}/jobs — jobs scoped to one facility. */
export const fetchFacilityJobs = (facilityId, paging) =>
  req({ url: withPaging(`${ADMIN}/facilities/${encodeURIComponent(facilityId)}/jobs`, paging), method: 'GET' });

/** GET /lms/jobs/{id}/errors — per-record failures for a job. */
export const fetchJobErrors = (jobId) =>
  req({ url: `${LMS}/jobs/${encodeURIComponent(jobId)}/errors`, method: 'GET' });

/**
 * POST /lms/jobs/{id}/retry — re-run a FAILED or COMPLETED_WITH_ERRORS bulk job
 * over the current delta. Idempotent at the Canvas SIS layer; returns the new
 * job acceptance (QUEUED/COMPLETED).
 */
export const retryJob = (jobId) =>
  req({ url: `${LMS}/jobs/${encodeURIComponent(jobId)}/retry`, method: 'POST', body: JSON.stringify({}) });

/**
 * POST /staff/{personId}/provision-canvas — provision a staff member to Canvas
 * as a FACILITY_ADMIN across their active facility admin memberships. Idempotent
 * server-side; safe to re-click.
 */
export const provisionStaffToCanvas = (personId) =>
  req({
    url: `${ADMIN}/staff/${encodeURIComponent(personId)}/provision-canvas`,
    method: 'POST',
    body: JSON.stringify({}),
  });

/**
 * GET /staff/{personId}/provision-canvas — per-facility Canvas admin
 * provisioning snapshot for the staff profile. Drives the "Provisioned to
 * Canvas" badge that replaces the still-clickable button once every
 * active FACILITY_ADMIN membership has been granted in Canvas.
 *
 * Returns `{ personId, totalFacilities, provisionedFacilities,
 * allProvisioned, facilities: [{ locationKey, locationName, provisioned,
 * lmsAdminGrantId }] }`.
 */
export const fetchStaffCanvasStatus = (personId) =>
  req({
    url: `${ADMIN}/staff/${encodeURIComponent(personId)}/provision-canvas`,
    method: 'GET',
  });
